import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, ChevronLeft, ChevronRight,
  Copy, Trash2, Send, Loader2,
  Mail, X, Pencil
} from 'lucide-react';
import { campaignApi } from '../services/emailApi';
import { useToast } from '../shared/ToastProvider';
import ConfirmModal from '../shared/ConfirmModal';
import { useUserContext, canWrite } from '../../../context/userContext';
import { useWallet } from '../../../context/walletContext';
import './EmailCampaigns.css';

const STATUS_MAP = {
  draft: { label: 'Draft', badge: 'neutral' },
  scheduled: { label: 'Scheduled', badge: 'info' },
  pending: { label: 'Pending', badge: 'info' },
  generating: { label: 'Generating', badge: 'warning' },
  sending: { label: 'Sending', badge: 'warning' },
  paused: { label: 'Paused', badge: 'danger' },
  sent: { label: 'Sent', badge: 'success' },
  completed: { label: 'Completed', badge: 'success' },
  failed: { label: 'Failed', badge: 'danger' },
};

const EmailCampaigns = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { userCtx } = useUserContext();
  const { planStatus, loadingPlanStatus, refreshPlanStatus } = useWallet();
  const canWriteCampaigns = !userCtx || !userCtx.pages || canWrite(userCtx, 'email_campaigns');

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [dupModal, setDupModal] = useState(null);
  const [dupName, setDupName] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: '', id: null });
  const [costEstimate, setCostEstimate] = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  const fetchCampaigns = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: pagination.limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const res = await campaignApi.list(params);
      if (res.data?.success) {
        const d = res.data.data;
        setCampaigns(Array.isArray(d) ? d : d?.campaigns || d?.rows || []);
        const pag = res.data.pagination || d?.pagination || {};
        const total = pag.total ?? pag.totalCount ?? pag.total_count ?? 0;
        const totalPages = pag.totalPages ?? pag.total_pages ?? (total > 0 ? Math.ceil(total / pagination.limit) : 0);
        setPagination(prev => ({ ...prev, ...pag, page, total, totalPages }));
      }
    } catch {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, pagination.limit, toast]);

  useEffect(() => { fetchCampaigns(1); }, [fetchCampaigns]);

  /* ── Actions ──────────────────────────────── */
  const withActionLoading = async (id, action, fn) => {
    setActionLoading(prev => ({ ...prev, [`${id}_${action}`]: true }));
    try {
      await fn();
      fetchCampaigns(pagination.page);
    } catch (err) {
      toast.error(err?.response?.data?.error || `${action} failed`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${id}_${action}`]: false }));
    }
  };

  const handleSend = async (id) => {
    setCostEstimate(null);
    setCostLoading(true);
    setConfirmModal({ open: true, type: 'send', id });
    try {
      const res = await campaignApi.costEstimate(id);
      if (res.data?.success && res.data?.data) {
        setCostEstimate(res.data.data);
      }
    } catch (err) {
      console.error('Cost estimate failed:', err);
    } finally {
      setCostLoading(false);
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({ open: true, type: 'delete', id });
  };

  const handleConfirmAction = () => {
    const { type, id } = confirmModal;
    setConfirmModal({ open: false, type: '', id: null });
    if (type === 'send') {
      withActionLoading(id, 'send', async () => {
        const res = await campaignApi.send(id);
        if (res.data?.success) {
          const usage = res.data.usage || res.data.plan_usage;
          if (usage) {
            toast.success(`Campaign sent! ${Number(usage.emails_used || 0).toLocaleString()} of ${Number(usage.email_limit || 0).toLocaleString()} emails used this cycle`);
          } else {
            toast.success(res.data.message || 'Campaign queued successfully');
          }
          if (res.data.info) {
            setTimeout(() => toast.info(res.data.info), 600);
          }
          refreshPlanStatus();
        }
        else toast.error(res.data?.error || 'Failed');
      });
    } else if (type === 'delete') {
      withActionLoading(id, 'delete', async () => {
        const res = await campaignApi.remove(id);
        if (res.data?.success) toast.success('Campaign deleted');
      });
    }
  };



  const handleDuplicate = async () => {
    if (!dupName.trim()) { toast.error('Enter a name'); return; }
    setDuplicating(true);
    try {
      const res = await campaignApi.duplicate(dupModal, dupName.trim());
      if (res.data?.success) {
        toast.success('Campaign duplicated');
        setDupModal(null);
        fetchCampaigns(pagination.page);
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Duplicate failed');
    } finally {
      setDuplicating(false);
    }
  };

  const isActionLoading = (id, action) => !!actionLoading[`${id}_${action}`];

  const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const hasFilters = search || statusFilter;

  return (
    <div className="emcp-page">
      {/* Header */}
      <div className="emcp-header">
        <div>
          <h1 className="emcp-title">Email Campaigns</h1>
          <p className="emcp-subtitle">{pagination.total} campaign{pagination.total !== 1 ? 's' : ''}</p>
        </div>
        <div className="emcp-header-actions">
          {/* Email Quota Badge — matches WhatsApp module style */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: '#f8f9fa',
            padding: '8px 16px',
            borderRadius: '8px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            <Mail size={16} style={{ color: '#ff8b18', flexShrink: 0 }} />
            <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
              {loadingPlanStatus ? (
                <div style={{
                  height: 16, width: 80,
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  backgroundSize: '200% 100%',
                  borderRadius: 4,
                  animation: 'emcp-skeleton-pulse 1.5s ease-in-out infinite',
                  display: 'inline-block',
                }} />
              ) : planStatus?.has_active_plan ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    background: '#e6f7ff', color: '#1890ff',
                    padding: '2px 8px', borderRadius: '4px',
                    fontSize: '12px', fontWeight: 600,
                  }}>
                    {planStatus.display_name || planStatus.plan_name || 'Active Plan'}
                  </span>
                  <span style={{ fontSize: '13px' }}>
                    {(planStatus.email?.remaining ?? 0).toLocaleString('en-IN')} Emails Left
                  </span>
                </span>
              ) : (
                <span style={{ color: '#999', fontSize: '13px' }}>No Active Plan</span>
              )}
            </span>
            {planStatus?.has_active_plan && planStatus?.days_remaining != null && planStatus.days_remaining <= 7 && (
              <button
                className="em-btn em-btn-sm"
                onClick={() => navigate('/dashboard/pricing')}
                style={{
                  borderRadius: '6px', fontSize: '12px',
                  background: '#fa8c16', color: '#fff',
                  border: 'none', padding: '3px 10px',
                }}
              >
                Renew ({planStatus.days_remaining}d left)
              </button>
            )}
          </div>
          <button className="em-btn em-btn-primary emcp-create-btn--header" onClick={() => navigate('/dashboard/email/campaigns/new')} disabled={!canWriteCampaigns}>
            <Plus size={16} />
            <span>New Campaign</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="emcp-filters">
        <div className="emcp-toolbar-main">
          <div className="emcp-search-wrap">
            <Search size={16} className="emcp-search-icon" />
            <input className="em-input emcp-search" placeholder="Search campaigns..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="em-btn em-btn-primary emcp-create-btn--toolbar" onClick={() => navigate('/dashboard/email/campaigns/new')} disabled={!canWriteCampaigns}>
            <Plus size={14} />
            <span>New Campaign</span>
          </button>
        </div>
        <select className="em-select emcp-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {Object.keys(STATUS_MAP).map(s => <option key={s} value={s}>{STATUS_MAP[s].label}</option>)}
        </select>
        {hasFilters && (
          <button className="em-btn em-btn-ghost em-btn-sm" onClick={() => { setSearch(''); setStatusFilter(''); }}><X size={14} /> Clear</button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="em-card" style={{ padding: 0 }}>
          <div className="em-table-wrap">
            <table className="em-table">
              <thead><tr><th>Campaign</th><th>Status</th><th>Recipients</th><th>Sent</th><th>Opens</th><th>Clicks</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {[0,1,2,3,4].map(i => (
                  <tr key={i}>
                    {[0,1,2,3,4,5,6,7].map(j => (
                      <td key={j}><div className="em-skl" style={{ height: 16, width: j === 0 ? '80%' : '60%' }} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="em-card">
          <div className="em-empty">
            <div className="em-empty-icon"><Mail size={24} /></div>
            <h3>{hasFilters ? 'No campaigns match your filters' : 'No campaigns yet'}</h3>
            <p>{hasFilters ? 'Try adjusting your search' : 'Create your first email campaign'}</p>
            {!hasFilters && canWriteCampaigns && (
              <button className="em-btn em-btn-primary" onClick={() => navigate('/dashboard/email/campaigns/new')}><Plus size={16} /> Create Campaign</button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="em-card" style={{ padding: 0 }}>
            <div className="em-table-wrap">
              <table className="em-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Recipients</th>
                    <th>Sent</th>
                    <th>Opens</th>
                    <th>Clicks</th>
                    <th>Created</th>
                    <th style={{ width: 160, minWidth: 160 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(c => {
                    const st = STATUS_MAP[c.status] || { label: c.status, badge: 'neutral' };
                    return (
                      <tr key={c.campaign_uuid || c.id} className="emcp-row" onClick={() => navigate(`/dashboard/email/campaigns/${c.campaign_uuid || c.id}`)}>
                        <td>
                          <div className="emcp-cell-name">{c.name}</div>
                          <div className="emcp-cell-subject">{c.subject}</div>
                        </td>
                        <td>
                          <span className={`em-badge em-badge-${st.badge}`}>
                            <span className="em-badge-dot" />
                            {st.label}
                          </span>
                        </td>
                        <td className="em-font-mono">{(c.total_recipients || 0).toLocaleString()}</td>
                        <td className="em-font-mono">{(c.sent_count || 0).toLocaleString()}</td>
                        <td className="em-font-mono">{(c.opened_count || 0).toLocaleString()}</td>
                        <td className="em-font-mono">{(c.clicked_count || 0).toLocaleString()}</td>
                        <td className="em-text-sm em-text-muted">{fmtDate(c.created_at)}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="emcp-actions">
                            {c.status === 'draft' && canWriteCampaigns && (
                              <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" title="Send" onClick={() => handleSend(c.campaign_uuid || c.id)} disabled={isActionLoading(c.campaign_uuid || c.id, 'send')}>
                                {isActionLoading(c.campaign_uuid || c.id, 'send') ? <Loader2 size={14} className="spin-icon" /> : <Send size={14} />}
                              </button>
                            )}
                            {canWriteCampaigns && (
                            <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" title="Duplicate" onClick={() => { setDupModal(c.campaign_uuid || c.id); setDupName(`${c.name} (copy)`); }}>
                              <Copy size={14} />
                            </button>
                            )}
                            {(c.status === 'draft' || c.status === 'scheduled') && canWriteCampaigns && (
                              <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" title="Edit" onClick={() => navigate(`/dashboard/email/campaigns/${c.campaign_uuid || c.id}/edit`)}>
                                <Pencil size={14} />
                              </button>
                            )}
                            {(c.status === 'draft' || c.status === 'scheduled') && canWriteCampaigns && (
                              <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" title="Delete" onClick={() => handleDelete(c.campaign_uuid || c.id)} disabled={isActionLoading(c.campaign_uuid || c.id, 'delete')}>
                                {isActionLoading(c.campaign_uuid || c.id, 'delete') ? <Loader2 size={14} className="spin-icon" /> : <Trash2 size={14} />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="em-pagination" style={{ marginTop: '1rem' }}>
              <span className="em-pagination-info">Page {pagination.page} of {pagination.totalPages}</span>
              <div className="em-pagination-btns">
                <button className="em-page-btn" disabled={pagination.page <= 1} onClick={() => fetchCampaigns(pagination.page - 1)}><ChevronLeft size={14} /></button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let p = i + 1;
                  if (pagination.totalPages > 5) p = Math.max(1, Math.min(pagination.page - 2, pagination.totalPages - 4)) + i;
                  return <button key={p} className={`em-page-btn ${pagination.page === p ? 'active' : ''}`} onClick={() => fetchCampaigns(p)}>{p}</button>;
                })}
                <button className="em-page-btn" disabled={pagination.page >= pagination.totalPages} onClick={() => fetchCampaigns(pagination.page + 1)}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Duplicate Modal */}
      {dupModal && (
        <div className="em-modal-overlay" onClick={() => setDupModal(null)}>
          <div className="em-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="em-modal-header">
              <h3 className="em-modal-title">Duplicate Campaign</h3>
              <button className="em-btn em-btn-ghost em-btn-icon" onClick={() => setDupModal(null)}><X size={16} /></button>
            </div>
            <div className="em-modal-body">
              <div className="em-input-group">
                <label className="em-label">New Campaign Name</label>
                <input className="em-input" value={dupName} onChange={e => setDupName(e.target.value)} autoFocus />
              </div>
            </div>
            <div className="em-modal-footer">
              <button className="em-btn em-btn-secondary" onClick={() => setDupModal(null)}>Cancel</button>
              <button className="em-btn em-btn-primary" onClick={handleDuplicate} disabled={duplicating}>
                {duplicating ? <Loader2 size={14} className="spin-icon" /> : <Copy size={14} />}
                <span>Duplicate</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Confirm Modal (Send / Delete) */}
      <ConfirmModal
        open={confirmModal.open}
        variant={confirmModal.type === 'delete' ? 'danger' : 'warning'}
        title={confirmModal.type === 'delete' ? 'Delete Campaign' : 'Send Campaign'}
        message={confirmModal.type === 'delete'
          ? 'Are you sure you want to delete this campaign? This action cannot be undone.'
          : null}
        confirmLabel={confirmModal.type === 'delete' ? 'Delete' : (() => {
          const remaining = planStatus?.email?.remaining;
          const recipients = Number(costEstimate?.recipients || 0);
          if (remaining != null && remaining < recipients) return 'Insufficient Quota';
          return 'Send Now';
        })()}
        confirmDisabled={confirmModal.type === 'send' && (() => {
          const remaining = planStatus?.email?.remaining;
          const recipients = Number(costEstimate?.recipients || 0);
          return remaining != null && remaining < recipients;
        })()}
        onConfirm={handleConfirmAction}
        onCancel={() => { setConfirmModal({ open: false, type: '', id: null }); setCostEstimate(null); }}
      >
        {confirmModal.type === 'send' && (
          costLoading ? (
            <div style={{ textAlign: 'center', padding: '12px 0' }}>
              <span className="spin-icon" style={{ display: 'inline-flex' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              </span>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280' }}>Loading quota...</p>
            </div>
          ) : (costEstimate || planStatus) ? (
            <div style={{ margin: '12px 0 0', textAlign: 'left', background: '#f0f9ff', borderRadius: 8, padding: '14px 16px', fontSize: 13, lineHeight: 1.8 }}>
              {costEstimate?.campaign_name && (
                <div style={{ fontWeight: 600, marginBottom: 4, color: '#111827' }}>{costEstimate.campaign_name}</div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Recipients</span>
                <span style={{ fontWeight: 600 }}>{Number(costEstimate?.recipients || 0).toLocaleString()}</span>
              </div>
              {(() => {
                const quota = planStatus?.email || costEstimate?.email_quota;
                const remaining = quota?.remaining || 0;
                const recipients = Number(costEstimate?.recipients || 0);
                const afterSend = Math.max(0, remaining - recipients);
                const hasInsufficient = remaining < recipients;
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #e0f2fe', paddingTop: 6, marginTop: 6 }}>
                      <span style={{ color: '#111827', fontWeight: 600 }}>Email Quota</span>
                      <span style={{ fontWeight: 600, color: hasInsufficient ? '#dc2626' : '#059669' }}>{remaining.toLocaleString()} remaining</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#6b7280' }}>After Send</span>
                      <span style={{ fontWeight: 600, color: hasInsufficient ? '#dc2626' : '#059669' }}>{afterSend.toLocaleString()} remaining</span>
                    </div>
                    {hasInsufficient && (
                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 12, fontWeight: 500 }}>
                        Insufficient email quota! You need {recipients.toLocaleString()} emails but only {remaining.toLocaleString()} remain.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#6b7280' }}>Send this campaign? Emails will begin delivering immediately.</p>
          )
        )}
      </ConfirmModal>
    </div>
  );
};

export default EmailCampaigns;
